import { useEffect, useCallback } from "react";
import { useTransactionsStore } from "../stores/transactionsStore";
import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { useCardStatusStore } from "../stores/cardStatusStore";
import {
  getTransactions,
  getOnchainTransactionDetails,
  getPendingTransactionBalance,
} from "../api/api";
import { Transaction, OnchainTransactionDetailsResponse } from "../api/types";

const PAGE_LIMIT = 20;

interface UseGetTransactionsReturn {
  transactions: Transaction[];
  isLoading: boolean;
  loadingNextPage: boolean;
  error: string | null;
  hasNext: boolean;
  pendingTxnAmount: number;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
  resetError: () => void;
  fetchOnchainDetails: (
    transactionID: string
  ) => Promise<OnchainTransactionDetailsResponse>;
}

// Helper function to handle API calls with consistent error handling and loading states
const executeTransactionFetch = async (
  apiCall: () => Promise<any>,
  onSuccess: (response: any) => void,
  errorMessage: string = "Failed to fetch transactions",
  isNextPage: boolean = false
) => {
  const store = useTransactionsStore.getState();

  try {
    if (isNextPage) {
      store._setLoadingNextPage(true);
    } else {
      store._setLoading(true);
    }
    store._setError(null);

    const response = await apiCall();
    onSuccess(response);
  } catch (error) {
    console.error(`[useGetTransactions] ${errorMessage}:`, error);
    const message = error instanceof Error ? error.message : errorMessage;
    store._setError(message);
  } finally {
    if (isNextPage) {
      store._setLoadingNextPage(false);
    } else {
      store._setLoading(false);
    }
  }
};

// Helper function to create API call function
const createApiCall =
  (
    eoaAddress: string,
    cardID: string,
    limit: number = PAGE_LIMIT,
    cursor?: string
  ) =>
  () =>
    getTransactions(eoaAddress, cardID, limit, cursor);

// Helper function to handle initial fetch success
const handleInitialFetchSuccess = (response: any) => {
  const store = useTransactionsStore.getState();
  store._setTransactions(response.data.transactions);
  store._setPagination(response.data.pagination);
  // Note: initialFetchDone is now set in executeTransactionFetch finally block
};

// Helper function to handle load more success
const handleLoadMoreSuccess = (response: any) => {
  const store = useTransactionsStore.getState();
  store._appendTransactions(response.data.transactions);
  store._setPagination(response.data.pagination);
};

/**
 * Hook for managing transactions
 * Automatically fetches transactions on mount using SDK internal state
 * Provides pagination and onchain details functionality
 */
export const useGetTransactions = (): UseGetTransactionsReturn => {
  const {
    transactions,
    pendingTxnAmount,
    pagination,
    isLoading,
    loadingNextPage,
    error,
    initialFetchDone,
  } = useTransactionsStore();
  const { eoaAddress } = useSDKConfigStore();
  const { userCard } = useCardStatusStore();

  // Auto-fetch on initial mount
  useEffect(() => {
    console.log("useEffect triggered with:", {
      eoaAddress,
      cardID: userCard?.cardID,
      initialFetchDone,
      isLoading,
    });

    const fetchInitialTransactions = async () => {
      if (eoaAddress && userCard?.cardID && !initialFetchDone && !isLoading) {
        console.log(
          "fetching initial transactions",
          eoaAddress,
          userCard.cardID,
          initialFetchDone,
          isLoading
        );

        // Set initialFetchDone immediately to prevent race condition
        const store = useTransactionsStore.getState();
        store._setInitialFetchDone(true);

        const apiCall = createApiCall(eoaAddress, userCard.cardID);
        await executeTransactionFetch(
          apiCall,
          handleInitialFetchSuccess,
          "Failed to fetch initial transactions"
        );
      }
    };

    const fetchPendingTransactionAmount = async () => {
      if (eoaAddress && userCard?.cardID && !isLoading) {
        try {
          const response = await getPendingTransactionBalance(userCard.cardID);
          const store = useTransactionsStore.getState();
          store._setPendingTxnAmount(response.data.balances.usd / 100);
        } catch (error) {
          console.error(
            "[useGetTransactions] Failed to fetch pending transaction amount:",
            error
          );
          const message =
            error instanceof Error
              ? error.message
              : "Failed to fetch pending transaction amount";
          const store = useTransactionsStore.getState();
          store._setError(message);
        }
      }
    };

    fetchInitialTransactions();
    fetchPendingTransactionAmount();
  }, [eoaAddress, userCard?.cardID, initialFetchDone, isLoading]);

  const loadMore = useCallback(async () => {
    if (
      !pagination?.has_next ||
      !pagination.next_cursor ||
      isLoading ||
      loadingNextPage ||
      !eoaAddress ||
      !userCard?.cardID
    ) {
      return;
    }

    const apiCall = createApiCall(
      eoaAddress,
      userCard.cardID,
      PAGE_LIMIT,
      pagination.next_cursor
    );
    await executeTransactionFetch(
      apiCall,
      handleLoadMoreSuccess,
      "Failed to load more transactions",
      true
    );
  }, [pagination, isLoading, loadingNextPage, eoaAddress, userCard?.cardID]);

  const refetch = useCallback(async () => {
    if (!eoaAddress || !userCard?.cardID || isLoading) return;

    const apiCall = createApiCall(eoaAddress, userCard.cardID);
    await executeTransactionFetch(
      apiCall,
      handleInitialFetchSuccess,
      "Failed to refetch transactions"
    );
  }, [eoaAddress, userCard?.cardID, isLoading]);

  // Function to fetch on-chain transaction details
  const fetchOnchainDetails = useCallback(
    async (
      transactionID: string
    ): Promise<OnchainTransactionDetailsResponse> => {
      try {
        console.log(
          `Fetching onchain details for transaction: ${transactionID}`
        );
        const response = await getOnchainTransactionDetails(transactionID);
        return response;
      } catch (error) {
        console.error("[fetchOnchainDetails]", error);
        // Return empty data structure on error
        return { data: [] };
      }
    },
    []
  );

  // Function to reset error state
  const resetError = useCallback(() => {
    const store = useTransactionsStore.getState();
    store._setError(null);
  }, []);

  return {
    transactions,
    isLoading,
    loadingNextPage,
    error,
    hasNext: pagination?.has_next ?? false,
    pendingTxnAmount,
    loadMore,
    refetch,
    resetError,
    fetchOnchainDetails,
  };
};
